// Rendu PDF côté client pour le catalogue de l'Espace Pro.
//
// Pourquoi ne pas se contenter d'une <iframe> : le lecteur natif n'affiche que
// la PREMIÈRE page sur iOS Safari et sur la plupart des WebViews Android. Un
// catalogue de 60 pages y devenait donc une simple page de couverture. On
// rasterise nous-mêmes avec pdf.js — le moteur du lecteur de Firefox — page
// par page, ce qui donne le même résultat sur tous les appareils.
//
// pdf.js (~1 Mo + son worker) n'entre JAMAIS dans le bundle initial : tout
// passe par l'import dynamique ci-dessous, déclenché au premier aperçu affiché.

import type { PDFDocumentProxy } from 'pdfjs-dist';

type PdfjsModule = typeof import('pdfjs-dist');

/** Les 14 polices « standard » PDF ne sont pas embarquées dans les fichiers :
 *  pdf.js va chercher leur binaire ici. Le dossier est copié à la racine du
 *  build par le plugin `pdfjs-standard-fonts` (vite.config.ts). URL absolue :
 *  la requête part du worker, une URL relative s'y résoudrait contre
 *  `/assets/`. */
function standardFontDataUrl(): string {
  return `${window.location.origin}/pdfjs/standard_fonts/`;
}

/** pdf.js ≥ 4.2 utilise `Promise.withResolvers`, absent d'iOS Safari < 17.4 et
 *  de Chrome < 119. Sans ce filet, le catalogue resterait vide sur les
 *  téléphones un peu anciens — soit une partie des électriciens. */
function ensurePromiseWithResolvers(): void {
  const ctor = Promise as PromiseConstructor & { withResolvers?: unknown };
  if (typeof ctor.withResolvers === 'function') return;
  ctor.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

let modulePromise: Promise<PdfjsModule> | null = null;

/** Charge pdf.js et son worker à la demande. */
export function loadPdfjs(): Promise<PdfjsModule> {
  if (!modulePromise) {
    const pending = (async () => {
      ensurePromiseWithResolvers();
      const [lib, worker] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
      ]);
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })();
    modulePromise = pending;
    // Un échec réseau ne doit pas condamner la session : on repart de zéro au
    // prochain appel plutôt que de resservir la promesse rejetée.
    pending.catch(() => {
      if (modulePromise === pending) modulePromise = null;
    });
  }
  return modulePromise;
}

/**
 * Ouvre un document. L'appelant est responsable de son `destroy()`.
 */
export async function openPdfDocument(url: string): Promise<PDFDocumentProxy> {
  const lib = await loadPdfjs();
  return lib.getDocument({
    url,
    // Les catalogues pèsent souvent des dizaines de Mo : on lit par plages
    // HTTP (Supabase Storage les supporte) et on ne préfetche pas la suite du
    // fichier tant que le lecteur n'y est pas arrivé.
    disableAutoFetch: true,
    // Pas d'`eval()` dans le worker : reste compatible avec une CSP stricte.
    isEvalSupported: false,
    standardFontDataUrl: standardFontDataUrl(),
  }).promise;
}

// ------------------------------------------------------------------
// File d'attente
// ------------------------------------------------------------------

/** Rasteriser est coûteux (CPU + mémoire). Deux pages à la fois suffisent à
 *  garder le défilement fluide sans saturer un téléphone. */
const MAX_PARALLEL_RENDERS = 2;
let running = 0;
const waiting: Array<() => void> = [];

export async function withPdfSlot<T>(task: () => Promise<T>): Promise<T> {
  if (running >= MAX_PARALLEL_RENDERS) {
    await new Promise<void>((resolve) => {
      waiting.push(resolve);
    });
  }
  running += 1;
  try {
    return await task();
  } finally {
    running -= 1;
    waiting.shift()?.();
  }
}

// ------------------------------------------------------------------
// Vignettes (première page)
// ------------------------------------------------------------------

export interface PdfPreview {
  /** JPEG encodé en data-URL : le bitmap du canvas est rendu au navigateur
   *  aussitôt, seule l'image compressée reste en mémoire. */
  dataUrl: string;
  pageCount: number;
}

/** Largeur de rendu de la vignette, en pixels physiques. Couvre une carte de
 *  ~260 px de large sur un écran à 2× sans être flou. */
const PREVIEW_WIDTH = 520;

const previewCache = new Map<string, PdfPreview>();
const previewJobs = new Map<string, Promise<PdfPreview>>();

/** La vignette déjà calculée, si la grille a déjà été affichée. */
export function getCachedPdfPreview(url: string): PdfPreview | null {
  return previewCache.get(url) ?? null;
}

/** Rasterise la couverture d'un PDF. Mise en cache : revenir sur la grille
 *  n'entraîne aucun nouveau téléchargement. */
export function renderPdfPreview(url: string): Promise<PdfPreview> {
  const cached = previewCache.get(url);
  if (cached) return Promise.resolve(cached);

  const inFlight = previewJobs.get(url);
  if (inFlight) return inFlight;

  const job = withPdfSlot(async () => {
    const doc = await openPdfDocument(url);
    try {
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: PREVIEW_WIDTH / base.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Contexte canvas 2D indisponible');
      // Un PDF sans fond dessine sur du transparent : sans ce blanc, la
      // vignette sortirait sur fond noir une fois encodée en JPEG.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport }).promise;
      const preview: PdfPreview = {
        dataUrl: canvas.toDataURL('image/jpeg', 0.82),
        pageCount: doc.numPages,
      };
      canvas.width = 0;
      canvas.height = 0;
      previewCache.set(url, preview);
      return preview;
    } finally {
      void doc.destroy();
    }
  }).finally(() => {
    previewJobs.delete(url);
  });

  previewJobs.set(url, job);
  return job;
}
